import { UpsellFooter } from "./upsell-footer";
import styled from "styled-components";
import { colors } from "styles/color-tokens";
import { Icon } from "@dialogueconsulting/sked-ui";

// Simple Content wrapper (previously from react-bootstrap/lib/Tab)
const Content = styled.div``;
import { UpsellFeatureType } from "./types";
import { trialEntitlement } from "./plan-api";
import { useState } from "react";
import { toast } from "react-toastify";
import { add, format } from "date-fns";
import { Modal } from "@/components/modal/modal";
import { hideModal } from "actions/modal";
import { useDispatch, useSelector } from "react-redux";
import QuokkaDoneImage from "./images/quokka-done.svg";
import { getUser } from "selectors/commonSelectors";
import { User } from "features/user/types";
import { slPermission } from "utils/sl-permission";
import { Button } from "@/components/ui/button";
import { useTrackEvent } from "@/libs/analytics";

export const UpsellTrial = ({ feature }: { feature: UpsellFeatureType }) => {
  switch (feature) {
    case "social-listening": {
      return <SocialListeningTrial />;
    }
    case "report-builder-trial": {
      return <ReportBuilderTrial />;
    }
    default: {
      return <></>;
    }
  }
};

const SL_TRIAL_DAYS = 14;
export const SocialListeningTrial = () => {
  const user: User = useSelector(getUser);
  const missingEntitlements = slPermission.getMissingEntitlements(user);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // Current timestamp in seconds (UTC)
  const currentTimestampInSeconds = Math.floor(Date.now() / 1000);
  // Add 7 days worth of seconds (7 days * 24 hours * 60 minutes * 60 seconds)
  const sevenDaysLaterTimestamp =
    currentTimestampInSeconds + SL_TRIAL_DAYS * 24 * 60 * 60;
  const trackEvent = useTrackEvent();

  const endDate = format(
    add(new Date(), { days: SL_TRIAL_DAYS }),
    "EEE, dd MMM yyyy"
  );

  const onTrialClick = async () => {
    setIsLoading(true);

    try {
      const { status, message } = await trialEntitlement({
        // 7 days later in seconds
        expires_at: sevenDaysLaterTimestamp,
        entitlements: missingEntitlements,
      });

      if (status === "error") {
        toast.error(message);
        return;
      }

      if (status === "ok") {
        setIsSuccess(true);
      }

      trackEvent({
        eventName: "listening-click-trial-start-confirm",
        useServices: ["AMP"],
      });
    } catch {
      toast.error("Error starting trial");
    } finally {
      setIsLoading(false);
    }
  };

  // eslint-disable-next-line unicorn/consistent-function-scoping
  const onHide = () => {
    window.location.href = "/dashboard/insights-v2/social-listening/summary";
  };

  if (isSuccess) {
    return (
      <BaseUpsellModal
        maxWidth="780px"
        showClose={false}
        escActionHandler={onHide}
      >
        <MainContainer>
          <Content>
            <SuccessContainer>
              <QuokkaDoneImage />
              <SuccessContent>
                <h1>
                  Congratulations! Your <strong>Social Listening</strong> trial
                  has started!
                </h1>
                <p>
                  {`You now have full access to Sentiment, Audience, Competitors and Keywords for ${SL_TRIAL_DAYS} days!`}
                </p>
              </SuccessContent>
              <Button variant="blue-primary" size="md" onClick={onHide}>
                Complete
              </Button>
            </SuccessContainer>
          </Content>
        </MainContainer>
      </BaseUpsellModal>
    );
  }

  return (
    <BaseUpsellModal>
      <MainContainer>
        <Content>
          <UpsellTrialWrap>
            <LeftSection>
              <UpsellTrialHeader>
                Start Your <HighlightedText>Social Listening</HighlightedText>{" "}
                Trial
              </UpsellTrialHeader>
              <UpsellTrialDescription>
                {`You're about to start a free trial of Social Listening. Here's what you'll get:`}
              </UpsellTrialDescription>
              <TrialFeaturesList>
                <TrialFeatureItem>
                  <Icon name="check" size="lg" color={colors.primary500} />
                  <TrialFeatureText>
                    <strong>Full access</strong> to Social Listening features
                  </TrialFeatureText>
                </TrialFeatureItem>
                <TrialFeatureItem>
                  <Icon name="check" size="lg" color={colors.primary500} />
                  <TrialFeatureText>
                    Trial lasts <strong>{SL_TRIAL_DAYS} days</strong> (End date:{" "}
                    <strong>{endDate}</strong>)
                  </TrialFeatureText>
                </TrialFeatureItem>
                <TrialFeatureItem>
                  <Icon name="check" size="lg" color={colors.primary500} />
                  <TrialFeatureText>
                    <strong>
                      No billing changes or automatic upgrades at trial end.
                    </strong>{" "}
                    You&apos;ll resume your standard access.
                  </TrialFeatureText>
                </TrialFeatureItem>
              </TrialFeaturesList>
              <ButtonWrap>
                <Button
                  disabled={isLoading}
                  variant="blue-primary"
                  size="md"
                  onClick={onTrialClick}
                >
                  {isLoading && <i className="fa fa-spinner fa-spin" />}
                  Start Free Trial
                </Button>
              </ButtonWrap>
            </LeftSection>
            <RightSection>
              <img
                src="/assets/img/upsell/upsell-social-listening-trial.png"
                alt="social listening upsell"
                width="400"
                height="225"
              />
            </RightSection>
          </UpsellTrialWrap>
          <UpsellFooter
            hideViewAllPlan
            infoText="Any questions? "
            feature="unknown"
            helpChatTopic="upsell"
          />
        </Content>
      </MainContainer>
    </BaseUpsellModal>
  );
};

const REPORT_BUILDER_TRIAL_DAYS = 14;
export const ReportBuilderTrial = () => {
  const [isSuccess, setIsSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // Current timestamp in seconds (UTC)
  const currentTimestampInSeconds = Math.floor(Date.now() / 1000);
  const sevenDaysLaterTimestamp =
    currentTimestampInSeconds + REPORT_BUILDER_TRIAL_DAYS * 24 * 60 * 60;
  const trackEvent = useTrackEvent();

  const endDate = format(
    add(new Date(), { days: SL_TRIAL_DAYS }),
    "EEE, dd MMM yyyy"
  );

  const onTrialClick = async () => {
    setIsLoading(true);

    try {
      const { status, message } = await trialEntitlement({
        // 7 days later in seconds
        expires_at: sevenDaysLaterTimestamp,
        entitlements: ["reports-full"],
      });

      if (status === "error") {
        toast.error(message);
        return;
      }

      if (status === "ok") {
        setIsSuccess(true);
      }

      trackEvent({
        eventName: "report-builder-click-trial-start-confirm",
        useServices: ["AMP"],
      });
    } catch {
      toast.error("Error starting trial");
    } finally {
      setIsLoading(false);
    }
  };

  // eslint-disable-next-line unicorn/consistent-function-scoping
  const onHide = () => {
    window.location.href = "/dashboard/insights-v2/report-builder";
  };

  if (isSuccess) {
    return (
      <BaseUpsellModal
        maxWidth="780px"
        showClose={false}
        escActionHandler={onHide}
      >
        <MainContainer>
          <Content>
            <SuccessContainer>
              <QuokkaDoneImage />
              <SuccessContent>
                <h1>
                  Congratulations! Your <strong>Custom Reports</strong> trial
                  has started!
                </h1>
                <p>
                  {`You now have full access to Custom Reports for ${REPORT_BUILDER_TRIAL_DAYS} days!`}
                </p>
              </SuccessContent>
              <Button variant="blue-primary" size="md" onClick={onHide}>
                Complete
              </Button>
            </SuccessContainer>
          </Content>
        </MainContainer>
      </BaseUpsellModal>
    );
  }

  return (
    <BaseUpsellModal>
      <MainContainer>
        <Content>
          <UpsellTrialWrap>
            <LeftSection>
              <UpsellTrialHeader>
                Start Your <HighlightedText>Custom Reports</HighlightedText>{" "}
                Trial
              </UpsellTrialHeader>
              <UpsellTrialDescription>
                {`You're about to start a free trial of Custom Reports. Here's what you'll get:`}
              </UpsellTrialDescription>
              <TrialFeaturesList>
                <TrialFeatureItem>
                  <Icon name="check" size="lg" color={colors.primary500} />
                  <TrialFeatureText>
                    <strong>Full access</strong> to Custom Reports features
                  </TrialFeatureText>
                </TrialFeatureItem>
                <TrialFeatureItem>
                  <Icon name="check" size="lg" color={colors.primary500} />
                  <TrialFeatureText>
                    Trial lasts <strong>{SL_TRIAL_DAYS} days</strong> (End date:{" "}
                    <strong>{endDate}</strong>)
                  </TrialFeatureText>
                </TrialFeatureItem>
                <TrialFeatureItem>
                  <Icon name="check" size="lg" color={colors.primary500} />
                  <TrialFeatureText>
                    <strong>
                      No billing changes or automatic upgrades at trial end.
                    </strong>{" "}
                    You&apos;ll resume your standard access.
                  </TrialFeatureText>
                </TrialFeatureItem>
              </TrialFeaturesList>
              <ButtonWrap>
                <Button
                  disabled={isLoading}
                  variant="blue-primary"
                  size="md"
                  onClick={onTrialClick}
                >
                  {isLoading && <i className="fa fa-spinner fa-spin" />}
                  Start Free Trial
                </Button>
              </ButtonWrap>
            </LeftSection>
            <RightSection>
              <img
                src="/assets/img/upsell/upsell-custom-reports-trial.png"
                alt="custom reports upsell"
                width="400"
                height="225"
              />
            </RightSection>
          </UpsellTrialWrap>
          <UpsellFooter
            hideViewAllPlan
            infoText="Any questions? "
            feature="unknown"
            helpChatTopic="upsell"
          />
        </Content>
      </MainContainer>
    </BaseUpsellModal>
  );
};

const BaseUpsellModal = ({
  children,
  showClose = true,
  maxWidth,
  escActionHandler,
}: {
  children: React.ReactNode;
  showClose?: boolean;
  maxWidth?: string;
  escActionHandler?: () => void;
}) => {
  const dispatch = useDispatch();
  const close = () => {
    dispatch(hideModal());
  };

  const handleEsc = () => {
    close();
    escActionHandler?.();
  };

  return (
    <ModalComponent
      show={true}
      onHide={escActionHandler ? handleEsc : close}
      backdrop="static"
      keyboard={Boolean(escActionHandler)}
      className={maxWidth ? `max-w-[${maxWidth}]` : undefined}
    >
      {showClose && <StyledCloseButton onClick={close} />}
      {children}
    </ModalComponent>
  );
};

const StyledCloseButton = styled(Modal.CloseButton)`
  position: absolute;
  right: 16px;
  top: 16px;
`;

const ModalComponent = styled(Modal)<{ maxWidth?: string }>`
  &.modal-dialog {
    font-size: 14px;
    max-width: ${({ maxWidth }) => maxWidth || "1014px"};
    padding: 0;
    position: absolute;
    left: 50% !important; /** must use !important to override bootstrap*/
    top: 50% !important;
    margin: auto auto !important;
    transform: translate(-50%, -50%) !important;
    & > .modal-content {
      padding: 32px;
    }
  }
`;

const MainContainer = styled.div``;

const UpsellTrialWrap = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
`;

const LeftSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 280px;
    object-fit: contain;
  }
`;

const UpsellTrialHeader = styled.h1`
  margin: 0;
  color: var(--Primary-P700, #0e1680);
  font-family: "Gotham SSm A", "Gotham SSm B";
  font-size: 24px;
  font-style: normal;
  font-weight: 600;
  line-height: 32px; /* 133.333% */
`;

const UpsellTrialDescription = styled.p`
  color: var(--Text-T01, #333);

  /* Web App/P1 Regular (Text md Regular) */
  font-family: Inter;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px; /* 150% */
`;

const HighlightedText = styled.span`
  color: ${colors.primary500};
`;

const TrialFeaturesList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TrialFeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const TrialFeatureText = styled.span`
  color: var(--text-text-primary, #333);

  /* Web App/P1 Regular (Text md Regular) */
  font-family: Inter;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;

  strong {
    font-weight: 600;
  }
`;

const ButtonWrap = styled.div`
  width: 240px;

  button {
    width: 100%;
  }
`;

const SuccessContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
`;

const SuccessContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;

  h1,
  strong {
    color: var(--Primary-P700, #0e1680);
    text-align: center;
    font-family: "Gotham SSm A", "Gotham SSm B";
    font-size: 24px;
    font-style: normal;
    font-weight: 600;
    line-height: 32px; /* 133.333% */
    margin: 0;

    strong {
      color: ${colors.primary500};
    }
  }

  p {
    color: var(--Text-T01, #333);
    text-align: center;

    /* Web App/P1 Regular (Text md Regular) */
    font-family: Inter;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px; /* 150% */

    strong {
      font-weight: 600;
      color: ${colors.primary500};
    }
  }
`;
